import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "./CartContext";
import productData, { siteConfig } from "../Datas/productData";
import "../css/checkout.css";

const countries = [
  ["AU", "Australia"], ["AT", "Austria"], ["BE", "Belgium"], ["CA", "Canada"],
  ["CN", "China"], ["DK", "Denmark"], ["FI", "Finland"], ["FR", "France"],
  ["DE", "Germany"], ["HK", "Hong Kong SAR"], ["IE", "Ireland"], ["IT", "Italy"],
  ["JP", "Japan"], ["MO", "Macao SAR"], ["MY", "Malaysia"], ["NL", "Netherlands"],
  ["NZ", "New Zealand"], ["NO", "Norway"], ["PT", "Portugal"], ["SG", "Singapore"],
  ["KR", "South Korea"], ["ES", "Spain"], ["SE", "Sweden"], ["CH", "Switzerland"],
  ["TW", "Taiwan"], ["TH", "Thailand"], ["AE", "United Arab Emirates"],
  ["GB", "United Kingdom"], ["US", "United States"], ["VN", "Vietnam"],
];

const initialForm = { email: "", firstName: "", lastName: "", country: "HK", address: "", apartment: "", city: "", state: "", postalCode: "", terms: false };
const requiredFields = ["email", "firstName", "lastName", "country", "address", "city", "postalCode"];
const labels = { email: "Email address", firstName: "First name", lastName: "Last name", country: "Country or region", address: "Street address", city: "City", postalCode: "Postal code" };

const createOrderNumber = () => `${Date.now()}${Math.floor(Math.random() * 1e9).toString().padStart(9, "0")}`;

const CheckOut = () => {
  const { cartItems, cartSubtotal } = useCart();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const shipping = siteConfig.standardShipping;
  const total = Number((cartSubtotal + shipping).toFixed(2));
  const items = useMemo(() => cartItems.map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) })).filter((item) => item.product), [cartItems]);

  const updateField = ({ target: { name, value, type, checked } }) => {
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    requiredFields.forEach((field) => { if (!String(form[field] || "").trim()) nextErrors[field] = `${labels[field]} is required`; });
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Enter a valid email address";
    if (!form.terms) nextErrors.terms = "Confirm the terms to continue";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      window.requestAnimationFrame(() => document.getElementById(Object.keys(nextErrors)[0])?.focus());
      return;
    }
    setIsProcessing(true);
    const origin = window.location.origin;
    const params = new URLSearchParams({ orderNo: createOrderNumber(), amount: total.toFixed(2), currency: siteConfig.currency, email: form.email, success_url: `${origin}/payment?state=success`, cancel_url: `${origin}/payment?state=cancel`, title: items[0].product.name });
    window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
  };

  if (!items.length) return <main className="checkout-empty"><div><h1>Your bag is empty</h1><p>Add an item before beginning checkout.</p><Link className="button button--dark" to="/shop">Continue shopping</Link></div></main>;

  const field = (name, label, options = {}) => <div className={`checkout-field${options.full ? " checkout-field--full" : ""}`}><label htmlFor={name}>{label}{options.required ? " *" : ""}</label><input id={name} name={name} type={options.type || "text"} value={form[name]} onChange={updateField} autoComplete={options.autoComplete} aria-invalid={Boolean(errors[name])} />{errors[name] && <span role="alert">{errors[name]}</span>}</div>;

  return <main className="checkout-page">
    <header className="checkout-brand"><Link to="/">{siteConfig.brandName}</Link></header>
    <div className="checkout-layout">
      <form className="checkout-form" onSubmit={handleSubmit} noValidate><section className="checkout-step" aria-labelledby="checkout-title">
        <div className="checkout-step__heading"><h1 id="checkout-title">Contact and delivery</h1><p>Enter your delivery details. Payment is completed securely on the next page.</p></div>
        <div className="checkout-fields">
          {field("email", "Email address", { full: true, required: true, type: "email", autoComplete: "email" })}
          {field("firstName", "First name", { required: true, autoComplete: "given-name" })}
          {field("lastName", "Last name", { required: true, autoComplete: "family-name" })}
          <div className="checkout-field checkout-field--full"><label htmlFor="country">Country or region *</label><select id="country" name="country" value={form.country} onChange={updateField} autoComplete="country">{countries.map(([code, name]) => <option key={code} value={code}>{name}</option>)}</select></div>
          {field("address", "Street address", { full: true, required: true, autoComplete: "address-line1" })}
          {field("apartment", "Apartment, suite, etc.", { full: true, autoComplete: "address-line2" })}
          {field("city", "City", { required: true, autoComplete: "address-level2" })}
          {field("state", "State / province", { autoComplete: "address-level1" })}
          {field("postalCode", "Postal code", { required: true, autoComplete: "postal-code" })}
        </div>
        <label className="checkout-terms"><input id="terms" type="checkbox" name="terms" checked={form.terms} onChange={updateField} /><span>I agree to the <Link to="/terms" target="_blank">terms and conditions</Link> and acknowledge the <Link to="/privacy" target="_blank">privacy policy</Link>.</span></label>
        {errors.terms && <p className="checkout-terms__error" role="alert">{errors.terms}</p>}
        <button className="button button--dark checkout-primary" type="submit" disabled={isProcessing}>{isProcessing ? "Redirecting…" : `Pay now - ${siteConfig.formatPrice(total)}`}</button>
      </section></form>
      <aside id="checkout-order" className="checkout-order" aria-labelledby="checkout-order-title"><h2 id="checkout-order-title">Order summary</h2>
        <div className="checkout-order__items">{items.map((item) => <article key={`${item.id}-${item.size}-${item.color}`}><img src={item.product.image} alt="" width="88" height="132" /><div><h3>{item.product.name}</h3><p>{item.color} / {item.size}</p><p>Quantity: {item.quantity}</p></div><strong>{siteConfig.formatPrice(item.product.price * item.quantity)}</strong></article>)}</div>
        <dl><div><dt>Subtotal</dt><dd>{siteConfig.formatPrice(cartSubtotal)}</dd></div><div><dt>Shipping</dt><dd>{siteConfig.formatPrice(shipping)}</dd></div><div className="checkout-order__total"><dt>Total</dt><dd>{siteConfig.formatPrice(total)}</dd></div></dl>
        <p className="checkout-order__service">Visa, Mastercard, American Express, Apple Pay and Google Pay are supported by our payment partner.</p>
      </aside>
    </div>
  </main>;
};

export default CheckOut;
